import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import {
  MagnifyingGlassIcon,
  PencilSquareIcon,
  UserCircleIcon,
  EyeSlashIcon,
  NoSymbolIcon,
  CheckCircleIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";
import { debounce } from "lodash";
import Pagination from "../../../components/CustomPagination";
import TeamUsersFilterToolbar from "../../../components/TeamUsersFilterToolbar";
import TableExportMenu from "../../../components/Menus/TableExportMenu";
import { setSearchTextByPage } from "../../../redux-store/features/providerFiltersSlice";
import SkeletonLoader from "../../../components/SkeletonLoader";
import { UserDashboardStats } from "../../../components/Provider/UserDashboardStats";
import {
  useGetAllProviderOrg,
  useGetAllProviderUsers,
  useGetRequestMetrics,
  useHideOrShowFromDiscovery,
  useActivateOrDeactivateProvider,
  useMakeRequestContact,
  useMakePrimaryContact,
  useActivateOrDeactivateProviderUserContact,
} from "../../../custom-hooks/queries";
import { getAllProviderUsers } from "../../../api/api-methods";
import UsersListTable, {
  ActiveStatusCell,
  RequestContactBadge,
  formatCreatedDate,
} from "../../../components/UsersListTable";
import { getCategoryBadgeValue, CONTACT_CATEGORY } from "../../../utils/requestContactDesignations";
import { buildVendorContactMenuItems } from "../../../utils/vendorContactDesignationMenu";
import { vendorTypeColors } from "../../../utils/constants";
import { getLocationFromProvider } from "../../../utils/addressUtils";
import { getPortalAccessLabelFromVendor } from "../../../utils/roles";
import {
  getDiscoveryStatusLabel,
  shouldShowOrgStatusBadge,
} from "../../../utils/providerOrgStatus";
import ActivateOrDeactivateConfirmationModal from "../../../components/ActivateOrDeactivateConfirmationModal";
import { useUser } from "@clerk/clerk-react";
import {
  isLoggedInVendorTeamMember,
  teamMemberRowClassName,
} from "../../../utils/teamMemberSelf";
import TeamMemberYouBadge from "../../../components/TeamMemberYouBadge";
import { resolveVendorAdminTabSortParams } from "../../../utils/vendorAdminGridSort";
import { sortVendorAdminItems } from "../../../utils/adminGridClientSort";
import { selectActiveAdminTabItems } from "../../../utils/gridSortApiParams";
import { useAdminSortState } from "../../../custom-hooks/useAdminSortState";
import { useUserTableFilters } from "../../../custom-hooks/useUserTableFilters";
import { useTeamUsersFilterCatalog } from "../../../custom-hooks/useTeamUsersFilterCatalog";
import {
  buildUserTableFilterDefinitions,
  nextTriStateSortConfig,
} from "../../../utils/teamUsersTable";
import { fetchProviderUsersAdminCatalogPage } from "../../../utils/teamUsersCatalog";
import {
  VENDOR_USER_EXPORT_HEADERS,
  buildTeamUsersExportData,
} from "../../../utils/teamUsersExport";

const initialActivateDeactivateModal = {
  open: false,
  kind: null,
  row: null,
  targetActive: null,
};

const UsersList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { user } = useUser();
  const {
    filters: userFilters,
    handleFilterChange: handleUserFilterChange,
    handleClearFilters: handleClearUserFilters,
    hasFilterChanges: hasUserFilterChanges,
  } = useUserTableFilters(true);
  // const [expandedRow, setExpandedRow] = useState(null);
  const [activeTab, setActiveTab] = useState("organizations");
  const { sortConfig, currentPage, setCurrentPage, handleSort } =
    useAdminSortState(nextTriStateSortConfig);
  const searchInputRef = useRef(null);

  const vendorSearchQuery =
    useSelector((state) => state.providersFilters?.searchTextByPage?.["vendorOrganizations"]) ?? "";
  const userSearchQuery =
    useSelector((state) => state.providersFilters?.searchTextByPage?.["vendorUsers"]) ?? "";

  const currentSearchQuery = activeTab === "organizations" ? vendorSearchQuery : userSearchQuery;
  const [searchInputValue, setSearchInputValue] = useState(currentSearchQuery);
  const [isSearchOpenByUser, setIsSearchOpenByUser] = useState(false);
  // Expanded when there is a value (e.g. when returning from another page) or when the user opened the search bar
  const isSearchExpanded = searchInputValue.length > 0 || isSearchOpenByUser;
  useEffect(() => {
    setSearchInputValue(currentSearchQuery);
    setIsSearchOpenByUser(currentSearchQuery.length > 0);
  }, [currentSearchQuery, activeTab]);

  useEffect(() => {
    return () => {
      dispatch(setSearchTextByPage({ pageKey: "vendorOrganizations", searchText: "" }));
      dispatch(setSearchTextByPage({ pageKey: "vendorUsers", searchText: "" }));
      queryClient.removeQueries({ queryKey: ["providerOrg"] });
      queryClient.removeQueries({ queryKey: ["providerUsers"] });
    };
  }, [dispatch, queryClient]);

  const { orgSortParams, userSortParams, serverSortParams } =
    resolveVendorAdminTabSortParams(activeTab, sortConfig);

  const { data: providerOrgData, isLoading: isLoadingProviderOrg } =
    useGetAllProviderOrg(21, currentPage, vendorSearchQuery, orgSortParams);
  const {
    data: providerUsersData,
    isLoading: isLoadingProviderUsers,
  } = useGetAllProviderUsers(21, currentPage, userSearchQuery, userSortParams, userFilters);
  const { data: requestMetrics } = useGetRequestMetrics();

  const fetchUsersCatalogPage = useCallback(
    (page) => fetchProviderUsersAdminCatalogPage(page),
    []
  );
  const { catalogItems, totalUsers } = useTeamUsersFilterCatalog({
    queryKey: ["providerUsersAdminCatalog"],
    fetchPage: fetchUsersCatalogPage,
  });

  // Stats for UserDashboardStats: org/user totals ignore table filters (prototype behavior)
  const VENDOR_STATS = [
    { name: "Total Vendors", value: providerOrgData?.total_items ?? 0 },
    { name: "Total Users", value: totalUsers ?? 0 },
    { name: "Total Requests", value: requestMetrics?.total_requests ?? 0 },
    { name: "Active Requests", value: requestMetrics?.active_requests ?? 0 },
  ];

  const debouncedVendorSearch = useMemo(() =>
    debounce((search) => {
      dispatch(setSearchTextByPage({ pageKey: "vendorOrganizations", searchText: search }));
    }, 300), [dispatch]
  );
  const debouncedUserSearch = useMemo(() =>
    debounce((search) => {
      dispatch(setSearchTextByPage({ pageKey: "vendorUsers", searchText: search }));
    }, 300), [dispatch]
  );

  useEffect(() => () => debouncedVendorSearch.cancel(), [debouncedVendorSearch]);
  useEffect(() => () => debouncedUserSearch.cancel(), [debouncedUserSearch]);

  useEffect(() => setCurrentPage(1), [activeTab, setCurrentPage, userFilters, userSearchQuery, vendorSearchQuery]);

  useEffect(() => {
    if (isSearchExpanded && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchExpanded]);

  const [searchParams] = useSearchParams();
  const tab = searchParams.get("tab");
  const [activateDeactivateModal, setActivateDeactivateModal] = useState(initialActivateDeactivateModal);

  useEffect(() => {
    if (tab === "users") setActiveTab("users");
  }, [tab]);

  const orgItems = useMemo(() => providerOrgData?.items ?? [], [providerOrgData]);
  const userItems = useMemo(() => providerUsersData?.items ?? [], [providerUsersData]);
  const userFilterDefinitions = useMemo(
    () =>
      buildUserTableFilterDefinitions({
        items: catalogItems,
        roleLabel: "Role",
        portalAccessLabel: "Portal Access",
        getPortalAccessValue: getPortalAccessLabelFromVendor,
        isVendorContext: true,
        getVqqContactValue: (row) => getCategoryBadgeValue(row, CONTACT_CATEGORY.RFI),
        getAssessmentContactValue: (row) =>
          getCategoryBadgeValue(row, CONTACT_CATEGORY.ASSESSMENT),
      }),
    [catalogItems]
  );
  const activeData = selectActiveAdminTabItems(
    activeTab,
    orgItems,
    userItems
  );
  const sortedItems =
    activeTab === "organizations"
      ? sortVendorAdminItems(activeData, sortConfig, serverSortParams)
      : activeData;
  const handleViewVendor = useCallback((row) => navigate(`/provider/${row?.provider_id}/overview`), [navigate]);
  const handleManageVendor = useCallback((row) => navigate(`/Backend/Users/AddVendorAccount/${row?.provider_id}`), [navigate]);
  const handleAddNewAccount = () => navigate("/Backend/Users/AddVendorAccount");
  const handleAddNewUser = () => navigate("/Backend/Users/AddProviderUser");
  const getUsersExportData = useCallback(
    () =>
      buildTeamUsersExportData({
        headers: VENDOR_USER_EXPORT_HEADERS,
        fetchPage: (page) =>
          getAllProviderUsers(200, page, userSearchQuery, userSortParams, userFilters),
        mapOptions: {
          includeOrganization: true,
          getPortalAccessValue: (row) =>
            row?.portal_access ?? getPortalAccessLabelFromVendor(row),
          includeVendorContacts: true,
          getVqqContactValue: (row) =>
            getCategoryBadgeValue(row, CONTACT_CATEGORY.RFI),
          getAssessmentContactValue: (row) =>
            getCategoryBadgeValue(row, CONTACT_CATEGORY.ASSESSMENT),
          formatCreated: formatCreatedDate,
        },
      }),
    [userFilters, userSearchQuery, userSortParams]
  );

  const { mutate: mutateHideOrShowDiscovery } = useHideOrShowFromDiscovery();
  const { mutate: mutateActivateOrDeactivateProvider } = useActivateOrDeactivateProvider();
  const { mutate: mutateMakeRequestContact } = useMakeRequestContact();
  const { mutate: mutateMakePrimaryContact } = useMakePrimaryContact();
  const { mutate: mutateActivateOrDeactivateProviderUserContact } = useActivateOrDeactivateProviderUserContact();

  const handleShowToDiscovery = useCallback((row) => {
    if (!row?.provider_id) return;
    mutateHideOrShowDiscovery({ provider_id: row.provider_id, is_visible: true });
  }, [mutateHideOrShowDiscovery]);

  const handleHideFromDiscovery = useCallback((row) => {
    if (!row?.provider_id) return;
    mutateHideOrShowDiscovery({ provider_id: row.provider_id, is_visible: false });
  }, [mutateHideOrShowDiscovery]);

  const openProviderActivateDeactivateModal = useCallback((row, targetActive) => {
    if (!row?.provider_id) return;
    setActivateDeactivateModal({ open: true, kind: "provider", row, targetActive });
  }, []);

  const handleActivateAccount = useCallback((row) => {
    openProviderActivateDeactivateModal(row, true);
  }, [openProviderActivateDeactivateModal]);

  const handleDeactivateAccount = useCallback((row) => {
    openProviderActivateDeactivateModal(row, false);
  }, [openProviderActivateDeactivateModal]);

  // Organization columns (prototype: Name, Primary Location, Type, Active Status, Discovery Status, Portal Access, Users, Requests, Created)
  const organizationColumns = useMemo(
    () => [
      {
        key: "name",
        header: "Name",
        sortable: true,
        width: "18%",
        cellClassName: "text-gray-900",
        render: (item) => {
          const logoUrl =
            item?.logo ||
            item?.logo_url ||
            item?.provider_logo_url ||
            item?.organization_logo_url ||
            item?.vendor_logo ||
            item?.provider_logo;
          return (
          <div className="flex items-center gap-2">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt=""
                  className="h-8 w-8 rounded-full object-cover flex-shrink-0"
                />
              ) : (
                  <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-green-100 text-sm font-medium text-green-600">
              {(item?.name || item?.provider_name || "?")[0].toUpperCase()}
            </div>
              )}
            <span
              className="text-sm font-medium text-gray-900 hover:text-indigo-600 break-words transition-colors cursor-pointer"
              onClick={() => handleViewVendor(item)}
            >
              {item?.name || item?.provider_name || "—"}
            </span>
          </div>
        )},
      },
      {
        key: "primary_location",
        header: "Primary Location",
        sortable: true,
        width: "14%",
        cellClassName: "text-gray-600",
        render: (item) => {
          return getLocationFromProvider(
            item?.provider_locations?.find(loc => loc.location_type === "primary"),
            { short: false, abbvState: true }
          ) || "—";
        },
      },
      {
        key: "provider_service_type",
        header: "Type",
        sortable: true,
        width: "12%",
        cellClassName: "text-gray-700",
        render: (item) => (
          <div className="flex items-center gap-2">
            <div className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${vendorTypeColors[item?.provider_service_type] || "bg-purple-500"}`} />
            <span>{item?.provider_service_type || "—"}</span>
          </div>
        ),
      },
      {
        key: "is_active",
        header: "Active Status",
        sortable: true,
        width: "10%",
        render: (item) => (
          <ActiveStatusCell isActive={item?.is_active} lastActivityAt={item?.last_login_datetime ?? item?.lastlogin} />
        ),
      },
      {
        key: "discovery_status",
        header: "Discovery Status",
        sortable: true,
        width: "10%",
        render: (item) => {
          if (!shouldShowOrgStatusBadge(item?.discovery_status)) {
            return <span className="text-gray-400">—</span>;
          }
          return (
            <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${item.discovery_status
              ? 'bg-blue-100 text-blue-700'
              : 'bg-yellow-100 text-yellow-700'
              }`}>
              {getDiscoveryStatusLabel(item.discovery_status)}
            </span>
          );
        },
      },
      {
        key: "portal_access",
        header: "Portal Access",
        sortable: true,
        width: "10%",
        cellClassName: "text-gray-700",
        render: (item) => item?.portal_access ?? getPortalAccessLabelFromVendor(item) ?? "—",
      },
      {
        key: "users_count",
        header: "Users",
        sortable: true,
        width: "6%",
        cellClassName: "text-gray-700",
        render: (item) => item?.users_count ?? "—",
      },
      {
        key: "requests_count",
        header: "Requests",
        sortable: true,
        width: "6%",
        cellClassName: "text-gray-700",
        render: (item) => item?.requests_count ?? "—",
      },
      {
        key: "created",
        header: "Created",
        sortable: true,
        width: "10%",
        cellClassName: "text-gray-700",
        render: (item) => formatCreatedDate(item?.created_at ?? item?.created_date ?? item?.create_date_time),
      },
    ],
    [handleViewVendor]
  );

  const organizationActionMenuItems = useMemo(
    () => [
      { label: "Manage Organization", icon: <PencilSquareIcon className="h-5 w-5" />, onClick: handleManageVendor },
      { label: "View Public Profile", icon: <UserCircleIcon className="h-5 w-5" />, onClick: handleViewVendor },
      { label: "Show to Discovery", icon: <EyeIcon className="h-5 w-5" />, onClick: handleShowToDiscovery, showItem: (item) => !item.discovery_status },
      { label: "Hide from Discovery", icon: <EyeSlashIcon className="h-5 w-5" />, onClick: handleHideFromDiscovery, showItem: (item) => item.discovery_status },
      { label: "Activate Account", icon: <CheckCircleIcon className="h-5 w-5" />, onClick: handleActivateAccount, showItem: (item) => !item.is_active },
      { label: "Deactivate Account", icon: <NoSymbolIcon className="h-5 w-5" />, onClick: handleDeactivateAccount, showItem: (item) => item.is_active },
    ],
    [handleManageVendor, handleViewVendor, handleShowToDiscovery, handleHideFromDiscovery, handleActivateAccount, handleDeactivateAccount]
  );

  // User columns (prototype: Name, Organization, Title, Email, Active Status, Role, Portal Access, Request Contact, Created)
  const userColumns = useMemo(
    () => [
      {
        key: "name",
        header: "Name",
        sortable: true,
        width: "14%",
        cellClassName: "text-gray-900",
        render: (item) => (
          <div className="flex items-center gap-2">
            <span
              className="text-sm font-medium text-gray-900 break-words transition-colors"
            >
              {[item?.first_name, item?.last_name].filter(Boolean).join(" ") || "—"}
            </span>
            {isLoggedInVendorTeamMember(item, user?.id) && (
              <TeamMemberYouBadge />
            )}
          </div>
        ),
      },
      {
        key: "provider_name",
        header: "Organization",
        sortable: true,
        width: "14%",
        cellClassName: "text-gray-700",
        render: (item) => {
          const logoUrl =
            item?.logo ||
            item?.logo_url ||
            item?.provider_logo_url ||
            item?.organization_logo_url ||
            item?.vendor_logo ||
            item?.provider_logo;
          return (
          <div className="flex items-center gap-2">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt=""
                className="h-6 w-6 rounded-full object-cover flex-shrink-0"
              />
            ) : (
              <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-green-100 text-sm font-medium text-green-600">
                {(item?.provider_name || "?")?.trim()?.[0]}
              </div>
            )}
            <span>{item?.provider_name?.trim() || "—"}</span>
          </div>
        )},
      },
      {
        key: "title",
        header: "Title",
        sortable: true,
        width: "12%",
        cellClassName: "text-gray-700",
        render: (item) => item?.job_title || item?.title || "—",
      },
      {
        key: "email",
        header: "Email",
        sortable: true,
        width: "14%",
        cellClassName: "text-gray-700",
        render: (item) => (
          <a href={`mailto:${item?.email}`} className="text-gray-900 hover:text-indigo-600">
            {item?.email || "—"}
          </a>
        ),
      },
      {
        key: "is_active",
        header: "Active Status",
        sortable: true,
        width: "10%",
        render: (item) => (
          <ActiveStatusCell
            isActive={item?.is_active}
            lastActivityAt={item?.last_login_datetime ?? item?.lastlogin}
          />
        ),
      },
      {
        key: "role",
        header: "Role",
        sortable: true,
        width: "10%",
        cellClassName: "text-gray-700",
        render: (item) => item?.sub_roles?.join?.("/") ?? item?.vendor_subrole_dto?.map((r) => r.sub_role).join("/") ?? "—",
      },
      {
        key: "portal_access",
        header: "Portal Access",
        sortable: true,
        width: "8%",
        cellClassName: "text-gray-700",
        render: (item) => item?.portal_access ?? getPortalAccessLabelFromVendor(item) ?? "—",
      },
      {
        key: "vqq_contact",
        header: "VQQ Contact",
        sortable: false,
        width: "8%",
        render: (row) => (
          <RequestContactBadge
            value={getCategoryBadgeValue(row, CONTACT_CATEGORY.RFI)}
            variant="vqq"
          />
        ),
      },
      {
        key: "assessment_contact",
        header: "Assessment Contact",
        sortable: false,
        width: "10%",
        render: (row) => (
          <RequestContactBadge
            value={getCategoryBadgeValue(row, CONTACT_CATEGORY.ASSESSMENT)}
            variant="assessment"
          />
        ),
      },
      {
        key: "created_at",
        header: "Created",
        sortable: true,
        width: "10%",
        cellClassName: "text-gray-700",
        render: (item) => formatCreatedDate(item?.created_at ?? item?.created_date ?? item?.create_date_time),
      },
    ],
    [user?.id]
  );



  const handleManageUser = useCallback((row) => {
    if (isLoggedInVendorTeamMember(row, user?.id)) {
      navigate("/Backend/Users/Me");
      return;
    }
    navigate(
      `/Backend/Users/AddProviderUser?${new URLSearchParams(
        Object.fromEntries(
          Object.entries({
            userId: row?.provider_user_id,
            providerName: row?.provider_name,
            clerkId: row?.provider_user_clerk_id,
            providerId: row?.provider_id,
          }).map(([k, v]) => [k, v ?? ""])
        )
      ).toString()}`
    );
  }, [navigate, user?.id]);

  const handleToggleCategoryRequestContact = useCallback((row, category, status) => {
    if (!row?.provider_user_id) return;
    mutateMakeRequestContact({
      provider_user_id: row.provider_user_id,
      request_contact_status: status,
      category,
    });
  }, [mutateMakeRequestContact]);

  const handleCategoryPrimaryContact = useCallback((row, category) => {
    if (!row?.provider_user_id) return;
    const primaryField = category === CONTACT_CATEGORY.RFI ? "rfi_is_primary" : "assessment_is_primary";
    if (row[primaryField]) return;
    mutateMakePrimaryContact({
      provider_id: row.provider_id,
      provider_user_id: row.provider_user_id,
      category,
    });
  }, [mutateMakePrimaryContact]);

  const openProviderUserActivateDeactivateModal = useCallback((row, targetActive) => {
    if (!row?.provider_user_id) return;
    setActivateDeactivateModal({ open: true, kind: "providerUser", row, targetActive });
  }, []);

  const handleActivateContact = useCallback((row) => {
    openProviderUserActivateDeactivateModal(row, true);
  }, [openProviderUserActivateDeactivateModal]);

  const handleDeactivateContact = useCallback((row) => {
    openProviderUserActivateDeactivateModal(row, false);
  }, [openProviderUserActivateDeactivateModal]);

  const handleConfirmActivateDeactivate = useCallback(() => {
    const { kind, row, targetActive } = activateDeactivateModal;
    if (!row || targetActive == null) return;
    if (kind === "provider") {
      mutateActivateOrDeactivateProvider({ provider_id: row.provider_id, is_active: targetActive });
    } else if (kind === "providerUser") {
      mutateActivateOrDeactivateProviderUserContact({
        provider_user_id: row.provider_user_clerk_id,
        is_active: targetActive,
      });
    }
    setActivateDeactivateModal(initialActivateDeactivateModal);
  }, [activateDeactivateModal, mutateActivateOrDeactivateProvider, mutateActivateOrDeactivateProviderUserContact]);

  const closeActivateDeactivateModal = useCallback(() => {
    setActivateDeactivateModal(initialActivateDeactivateModal);
  }, []);

  const activateDeactivateEntityName = useMemo(() => {
    const row = activateDeactivateModal.row;
    if (!row) return "";
    if (activateDeactivateModal.kind === "provider") {
      return row?.name || row?.provider_name || "—";
    }
    return [row?.first_name, row?.last_name].filter(Boolean).join(" ") || row?.email || "—";
  }, [activateDeactivateModal.row, activateDeactivateModal.kind]);

  const userActionMenuItems = useMemo(
    () => [
      {
        label: (row) =>
          isLoggedInVendorTeamMember(row, user?.id) ? "Manage my profile" : "Manage",
        icon: <PencilSquareIcon className="h-5 w-5" />,
        onClick: handleManageUser,
      },
      ...buildVendorContactMenuItems({
        allUsers: userItems,
        onToggleRequestContact: handleToggleCategoryRequestContact,
        onMakePrimary: handleCategoryPrimaryContact,
      }),
      { label: "Activate Account", icon: <CheckCircleIcon className="h-5 w-5" />, onClick: handleActivateContact, showItem: (item) => !item.is_active },
      {
        label: "Deactivate Account",
        icon: <NoSymbolIcon className="h-5 w-5" />,
        onClick: handleDeactivateContact,
        showItem: (item) =>
          item.is_active && !isLoggedInVendorTeamMember(item, user?.id),
      },
    ],
    [
      handleManageUser,
      userItems,
      handleToggleCategoryRequestContact,
      handleCategoryPrimaryContact,
      handleActivateContact,
      handleDeactivateContact,
      user?.id,
    ]
  );

  const isLoading = activeTab === "organizations" ? isLoadingProviderOrg : isLoadingProviderUsers;
  const totalPages =
    activeTab === "organizations"
      ? providerOrgData?.total_pages ?? 1
      : providerUsersData?.total_pages ?? 1;
  const isSearchActive = activeTab === "organizations" ? !!vendorSearchQuery : !!userSearchQuery;

  return (
    <div className="w-full min-h-screen bg-gray-50 px-4 sm:px-6 lg:px-8 pb-6">
      <div className="">
        <div className="shadow-header -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8">
          {/* Header - on gray background */}
          <div className="bg-white py-6 flex flex-col items-start justify-between -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8">
            <h1 className="text-lg font-medium text-gray-900">Vendors</h1>
            <p className="mt-1 text-sm text-gray-500">Overview of Vendor statistics and activities.</p>
          </div>

          {/* Stats - white band using UserDashboardStats */}
          <div className="">
            <UserDashboardStats stats={VENDOR_STATS} />
          </div>
        </div>

        {/* Tabs + search/options - on gray, no Add button here */}
        <div className="flex sm:flex-row sm:items-center justify-between gap-4 border-b border-gray-200 mb-4 mt-10 px-4 sm:px-6 lg:px-8">
          <nav className="flex gap-8 pt-4">
            <button
              onClick={() => setActiveTab("organizations")}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${activeTab === "organizations"
                ? "border-indigo-600 text-indigo-600"
                : "border-transparent text-gray-400 hover:text-gray-600 hover:border-gray-300"
                }`}
            >
              Organizations
            </button>
            <button
              onClick={() => setActiveTab("users")}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${activeTab === "users"
                ? "border-indigo-600 text-indigo-600"
                : "border-transparent text-gray-400 hover:text-gray-600 hover:border-gray-300"
                }`}
            >
              Users
            </button>
          </nav>
          <div className="flex items-center gap-3 pb-3">
            {/* Search: expand on click like TeamManagement */}
            <div
              className={`relative flex items-center transition-all duration-300 ease-in-out ${isSearchExpanded ? "w-64" : "w-auto"}`}
            >
              {isSearchExpanded ? (
                <div className="relative w-full">
                  <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder={activeTab === "organizations" ? "Search organizations..." : "Search users..."}
                    value={searchInputValue}
                    onChange={(e) => {
                      const v = e.target.value;
                      setSearchInputValue(v);
                      if (activeTab === "organizations") debouncedVendorSearch(v);
                      else debouncedUserSearch(v);
                    }}
                    onBlur={() => !searchInputValue && setIsSearchOpenByUser(false)}
                    className="block w-full rounded-md border-0 py-1.5 pl-9 pr-3 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm sm:leading-6"
                  />
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setIsSearchOpenByUser(true)}
                  className="p-2 text-gray-400 hover:text-indigo-600 transition-colors rounded-md hover:bg-gray-100"
                  aria-label="Search"
                >
                  <MagnifyingGlassIcon className="h-5 w-5" />
                </button>
              )}
            </div>
            {activeTab === "users" && (
              <TableExportMenu
                getData={getUsersExportData}
                reportTitle="Vendor Users"
                reportSubtitle="Current filtered users view"
                filePrefix="Vendor_Users"
                downloadType="Vendor Users"
              />
            )}
          </div>
        </div>

        {/* White card: Add button just above table + table */}
        <div className="bg-white shadow border border-gray-200 rounded-lg">
          <div className="px-4 pt-6 pb-3 sm:px-6 lg:px-8">
            {activeTab === "users" ? (
              <TeamUsersFilterToolbar
                filters={userFilterDefinitions}
                values={userFilters}
                onChange={handleUserFilterChange}
                onClear={handleClearUserFilters}
                showClearFilters={hasUserFilterChanges}
                actions={
                  <button
                    type="button"
                    onClick={handleAddNewUser}
                    className="rounded-md bg-indigo-700 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  >
                    Add User
                  </button>
                }
              />
            ) : (
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleAddNewAccount}
                  className="rounded-md bg-indigo-700 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                  Add Vendor
                </button>
              </div>
            )}
          </div>

          {isLoading ? (
            <SkeletonLoader />
          ) : (
              <UsersListTable
                className="shadow-sm mt-6"
                getRowClassName={(item) =>
                  teamMemberRowClassName(item, {
                    isMe: isLoggedInVendorTeamMember(item, user?.id),
                  })
                }
                columns={activeTab === "organizations" ? organizationColumns : userColumns}
                data={sortedItems}
                actionMenuItems={activeTab === "organizations" ? organizationActionMenuItems : userActionMenuItems}
                sortConfig={sortConfig}
                onSort={handleSort}
                rowKey={activeTab === "organizations" ? "provider_id" : "provider_user_id"}
                emptyMessage={`No ${activeTab === "organizations" ? "organizations" : "users"} found`}
                emptyDescription={
                  activeTab === "organizations"
                    ? "No vendor organizations to display."
                    : hasUserFilterChanges && !isSearchActive
                      ? "No users match the current filters"
                      : "No vendor users to display."
                }
                isSearchActive={isSearchActive}
            />
          )}

          {sortedItems.length > 0 && (
            <div className="border-t border-gray-200">
              <Pagination
                currentPage={currentPage}
                onPageChange={setCurrentPage}
                totalPages={totalPages}
              />
            </div>
          )}
        </div>
      </div>

      <ActivateOrDeactivateConfirmationModal
        open={activateDeactivateModal.open}
        onClose={closeActivateDeactivateModal}
        onConfirm={handleConfirmActivateDeactivate}
        type={activateDeactivateModal.kind === "providerUser" ? "user" : "vendor"}
        isDeactivated={activateDeactivateModal.targetActive === true}
        entityName={activateDeactivateEntityName}
      />
    </div>
  );
};

export default UsersList;
